import React, { useEffect, useState, useContext } from "react";
import { Context } from "../../context/Context"; // Import your context
import { jwtDecode } from "jwt-decode"; // Correct named import
import axios from "axios";

const UserKarnety = () => {
  const { token } = useContext(Context); // Get the token from context
  const [karnety, setKarnety] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKarnet, setSelectedKarnet] = useState(null);
  const [suspensionWeeks, setSuspensionWeeks] = useState(1);
  const [maxSuspensionWeeks, setMaxSuspensionWeeks] = useState(1);
  const [minSuspensionWeeks, setMinSuspensionWeeks] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(2); // Ilość karnetów na stronę

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchKarnety = async () => {
      if (!token) {
        console.error("No token found");
        setError("No token found. Please log in.");
        setLoading(false);
        return;
      }

      try {
        const decodedToken = jwtDecode(token);
        const userId = decodedToken._id;

        const response = await axios.get(
          `${BACKEND_URL}/api/karnet/getKarnetByUserId/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          // Calculate payment reminder information for each karnet
          const karnetyWithPaymentInfo = response.data.map((karnet) => {
            const paymentsDueCount = karnet
              ? karnet.completed_trainings === 0
                ? 0
                : Math.max(
                    karnet.przypomnienie_platnosc -
                      (karnet.completed_trainings %
                        karnet.przypomnienie_platnosc) -
                      2
                  )
              : 0;

            let czyWymaganaPlatnosc = false;
            let expectedInstallments = 0;
            let czyWiecej = false;
            let totalPayments = 0;

            if (karnet.platnosc_rodzaj === "rata" && karnet.ilosc_rat > 0) {
              const reminderInterval = karnet.przypomnienie_platnosc;
              const totalInstallments = karnet.ilosc_rat;
              const completedTrainings = karnet.completed_trainings;

              expectedInstallments = Math.min(
                Math.floor((completedTrainings + 2) / reminderInterval) + 1,
                Math.ceil(karnet.cena / karnet.cena_za_rate)
              );

              totalPayments = Math.ceil(karnet.cena / karnet.cena_za_rate);

              if (totalPayments - expectedInstallments < totalInstallments) {
                czyWymaganaPlatnosc = true;
              }
              if (totalPayments - expectedInstallments >= totalInstallments) {
                czyWiecej = true;
              }
            }

            return {
              ...karnet,
              paymentsDueCount,
              czyWymaganaPlatnosc,
              czyWiecej,
              expectedInstallments,
              totalPayments,
            };
          });

          setKarnety(karnetyWithPaymentInfo);
        } else {
          setError("No karnety found.");
        }
      } catch (err) {
        setError(
          `An error occurred while fetching karnety. ${
            err.response?.data?.message || err.message || "Unknown error"
          }`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchKarnety();
  }, [token]);

  const openModal = (karnet) => {
    setSelectedKarnet(karnet);
    const minWeeks = karnet.max_gwiazdek <= 4 ? 2 : 1;
    setMinSuspensionWeeks(minWeeks);
    setSuspensionWeeks(minWeeks);
    if (karnet.max_suspend) {
      setMaxSuspensionWeeks(karnet.max_suspend);
    } else {
      calculateMaxSuspensionWeeks(karnet);
    } // Calculate max weeks when opening modal
    setShowModal(true);
  };
  const handleUnsuspendKarnet = async (karnetId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/karnet/unsuspend/${karnetId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      alert("Karnet odblokowany pomyślnie.");
      window.location.reload();
    } catch (err) {
      alert(
        `Błąd podczas odblokowywania karnetu: ${
          err.response?.data?.message || err.message
        }`
      );
    }
  };

  const calculateMaxSuspensionWeeks = (karnet) => {
    const startDate = new Date(karnet.data_rozpoczecia);
    const endDate = new Date(karnet.data_zakonczenia);
    const karnetLengthMonths =
      (endDate - startDate) / (1000 * 60 * 60 * 24 * 30);
    const maxWeeks =
      karnetLengthMonths < 1 ? 1 : Math.floor((karnetLengthMonths * 4) / 3);
    setMaxSuspensionWeeks(maxWeeks);
    // Set minimum suspension weeks based on stars
  };

  const handleSuspendKarnet = async () => {
    if (
      suspensionWeeks <= minSuspensionWeeks ||
      suspensionWeeks >= maxSuspensionWeeks
    ) {
      alert(
        `Suspension time must be between ${minSuspensionWeeks} and ${maxSuspensionWeeks} weeks.`
      );
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/karnet/suspend/${selectedKarnet._id}`,
        {
          suspensionWeeks,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      alert("Karnet suspended successfully.");
      setShowModal(false);
    } catch (err) {
      alert(
        `Error suspending karnet: ${err.response?.data?.message || err.message}`
      );
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedKarnet(null);
  };

  // Paginacja
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = karnety.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg font-medium text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-4 h-full">
        <h2 className="text-3xl font-bold text-gray-800 text-center mb-6">
          Twoje Karnety
        </h2>
        {karnety.length === 0 ? (
          <p className="text-center text-gray-600 text-lg">
            No karnety found. Please purchase a karnet to see it here.
          </p>
        ) : (
          <div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {currentItems.map((karnet) => (
                <li
                  key={karnet._id}
                  className={`bg-blue-50 border border-gray-300 rounded-md shadow-sm p-3 max-w-md ${
                    karnet.platnosc_rodzaj === "rata" &&
                    (karnet.paymentsDueCount === 0 ||
                      karnet.czyWymaganaPlatnosc) &&
                    karnet.ilosc_rat > 0 &&
                    !karnet.czyWiecej
                      ? "border-red-500"
                      : ""
                  }`}
                >
                  <h3 className="text-xl font-semibold text-[#313131]">
                    {karnet.nazwa}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Rodzaj:</strong> {karnet.rodzaj}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Data Rozpoczęcia:</strong>{" "}
                    {new Date(karnet.data_rozpoczecia).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Data Zakończenia:</strong>{" "}
                    {new Date(karnet.data_zakonczenia).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Ilość Wejść:</strong> {karnet.ilosc_wejsc_kal}
                  </p>
                  {karnet.platnosc_rodzaj === "rata" && (
                    <>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Płatność za:</strong>{" "}
                        {karnet.paymentsDueCount === -1 && (
                          <span>
                            {karnet.przypomnienie_platnosc - 1} Trening
                          </span>
                        )}
                        {karnet.paymentsDueCount === -2 && (
                          <span>
                            {karnet.przypomnienie_platnosc - 2} Trening
                          </span>
                        )}
                        {karnet.paymentsDueCount !== -1 &&
                          karnet.paymentsDueCount !== -2 && (
                            <>
                              {Number.isInteger(karnet.paymentsDueCount)
                                ? `${karnet.paymentsDueCount} Trening`
                                : "0 Trening"}
                            </>
                          )}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Ilość Rat:</strong> {karnet.ilosc_rat}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Cena za Ratę:</strong> {karnet.cena_za_rate} PLN
                      </p>
                    </>
                  )}
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Aktywny:</strong>{" "}
                    {karnet.suspended
                      ? "Zawieszony"
                      : karnet.aktywny
                      ? "Aktywny"
                      : "Nieaktywny"}
                  </p>

                  <button
                    onClick={() => openModal(karnet)}
                    disabled={karnet.suspended || karnet.aktywny === false}
                    className={`mt-4 w-full ${
                      karnet.suspended || karnet.aktywny === false
                        ? "bg-gray-400 cursor-not-allowed py-2 px-4 rounded"
                        : "bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition"
                    }`}
                  >
                    Zawieś Karnet
                  </button>

                  {karnet.suspended && (
                    <button
                      onClick={() => handleUnsuspendKarnet(karnet._id)}
                      className="mt-2 w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition"
                    >
                      Odblokuj Karnet
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-center mt-6">
              {Array.from(
                { length: Math.ceil(karnety.length / itemsPerPage) },
                (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => paginate(i + 1)}
                    className={`mx-1 px-3 py-1 rounded ${
                      currentPage === i + 1
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-5 rounded shadow-lg">
            <h2 className="text-xl font-semibold">Zawieś Karnet</h2>
            <p>Minimalny okres: {minSuspensionWeeks} tygodni</p>
            <p>Maksymalny okres: {maxSuspensionWeeks} tygodni</p>
            <input
              type="number"
              value={suspensionWeeks}
              onChange={(e) => setSuspensionWeeks(Number(e.target.value))}
              min={minSuspensionWeeks}
              max={maxSuspensionWeeks}
              className="border rounded p-2 w-full"
            />
            <div className="flex justify-end mt-4">
              <button onClick={closeModal} className="mr-2 text-gray-500">
                Cancel
              </button>
              <button
                onClick={handleSuspendKarnet}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Zawieś
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserKarnety;
